import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import BasketballCourt from "@/components/BasketballCourt";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SHOT_TYPE_MAP } from "@/lib/zones";

export default function Reports() {
  const [scope, setScope] = useState("team");
  const [athletes, setAthletes] = useState([]);
  const [selectedAthlete, setSelectedAthlete] = useState("");
  const [stats, setStats] = useState(null);
  const [ranking, setRanking] = useState([]);

  useEffect(() => { api.get("/athletes").then(r => setAthletes(r.data)); }, []);

  useEffect(() => {
    (async () => {
      if (scope === "team") {
        const [t, r] = await Promise.all([api.get("/stats/team"), api.get("/stats/ranking")]);
        setStats(t.data); setRanking(r.data);
      } else if (selectedAthlete) {
        const s = await api.get(`/stats/athlete/${selectedAthlete}`);
        setStats(s.data); setRanking([]);
      } else {
        setStats(null);
      }
    })();
  }, [scope, selectedAthlete]);

  const exportPDF = async () => {
    const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
      import("jspdf"), import("html2canvas")
    ]);
    const node = document.getElementById("report-content");
    if (!node) return;
    const canvas = await html2canvas(node, { backgroundColor: "#0A0A0A", scale: 2 });
    const img = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "pt", "a4");
    const pageW = pdf.internal.pageSize.getWidth();
    const imgH = (canvas.height * pageW) / canvas.width;
    pdf.addImage(img, "PNG", 0, 0, pageW, imgH);
    const fname = scope === "team" ? "relatorio_equipe" : `relatorio_${athletes.find(a=>a.athlete_id===selectedAthlete)?.name || "atleta"}`;
    pdf.save(`${fname}.pdf`);
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-zinc-500">Relatórios</div>
            <h1 className="font-display font-black text-5xl uppercase tracking-tight mt-1">Análise & PDF</h1>
          </div>
          <div className="flex items-end gap-3 flex-wrap">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">Escopo</label>
              <Select value={scope} onValueChange={(v) => { setScope(v); setSelectedAthlete(""); }}>
                <SelectTrigger className="mt-2 bg-[#141414] border-zinc-800 text-white w-44" data-testid="report-scope">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#141414] border-zinc-800 text-white">
                  <SelectItem value="team">Equipe</SelectItem>
                  <SelectItem value="athlete">Atleta</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {scope === "athlete" && (
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">Atleta</label>
                <Select value={selectedAthlete} onValueChange={setSelectedAthlete}>
                  <SelectTrigger className="mt-2 bg-[#141414] border-zinc-800 text-white w-56" data-testid="report-athlete">
                    <SelectValue placeholder="Selecionar" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#141414] border-zinc-800 text-white">
                    {athletes.map(a => <SelectItem key={a.athlete_id} value={a.athlete_id}>{a.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <Button
              onClick={exportPDF}
              disabled={!stats}
              data-testid="export-report-pdf"
              className="bg-[#FF3B30] hover:bg-red-600 text-white font-bold uppercase tracking-wide rounded-sm"
            >
              Exportar PDF
            </Button>
          </div>
        </div>

        {!stats ? (
          <p className="text-zinc-500 text-sm">Selecione um escopo para gerar o relatório.</p>
        ) : (
          <div id="report-content" className="space-y-6">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <Kpi label="Aproveitamento" value={`${stats.pct}%`} accent />
              <Kpi label="Acertos" value={stats.total_made} />
              <Kpi label="Arremessos" value={stats.total_attempts} />
              <Kpi label="Pontos" value={stats.total_points} />
            </div>

            <div className="bg-[#141414] border border-zinc-800 rounded-md p-6">
              <h2 className="font-display font-bold text-2xl uppercase tracking-tight mb-4">Mapa de Calor</h2>
              <BasketballCourt zoneStats={stats.by_zone} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-[#141414] border border-zinc-800 rounded-md p-6">
                <h2 className="font-display font-bold text-2xl uppercase tracking-tight mb-4">Por Tipo de Arremesso</h2>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-zinc-500 uppercase text-xs tracking-wider">
                      <th className="text-left py-2">Tipo</th>
                      <th className="text-right">Acertos</th>
                      <th className="text-right">Total</th>
                      <th className="text-right">%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(stats.by_type).map(([k, v]) => (
                      <tr key={k} className="border-t border-zinc-800">
                        <td className="py-2">{SHOT_TYPE_MAP[k] || k}</td>
                        <td className="text-right">{v.made}</td>
                        <td className="text-right">{v.attempts}</td>
                        <td className="text-right font-bold">{v.attempts > 0 ? `${v.pct}%` : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {scope === "team" && ranking.length > 0 && (
                <div className="bg-[#141414] border border-zinc-800 rounded-md p-6">
                  <h2 className="font-display font-bold text-2xl uppercase tracking-tight mb-4">Ranking de Atletas</h2>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-zinc-500 uppercase text-xs tracking-wider">
                        <th className="text-left py-2">#</th>
                        <th className="text-left">Atleta</th>
                        <th className="text-right">%</th>
                        <th className="text-right">Arrem.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ranking.map((a, i) => (
                        <tr key={a.athlete_id} className="border-t border-zinc-800">
                          <td className="py-2 font-bold">{i + 1}</td>
                          <td>{a.name}</td>
                          <td className="text-right font-bold">{a.pct}%</td>
                          <td className="text-right">{a.attempts}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

function Kpi({ label, value, accent }) {
  return (
    <div className="bg-[#141414] border border-zinc-800 rounded-md p-5 relative overflow-hidden">
      {accent && <div className="absolute top-0 left-0 w-full h-0.5 bg-[#FF3B30]" />}
      <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">{label}</div>
      <div className="font-display font-black text-4xl mt-2 tracking-tighter">{value}</div>
    </div>
  );
}
